import Vue from 'vue'

// axios
import axios from 'axios'
import useJwt from '@/auth/jwt/useJwt'
import Router from '@/router'
import store from '@/store'
import i18n from '@/libs/i18n'
import ability from '@/libs/acl/ability'
import { initialAbility } from '@/libs/acl/config'
import { isUserLoggedIn } from '@/auth/utils'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const axiosIns = axios.create({
  // You can add your headers here
  // ================================
  // baseURL: 'https://some-domain.com/api/',
  // timeout: 1000,
  // headers: { 'X-Custom-Header': 'foobar' }
  headers: {
    'Content-Type': 'application/json'
  }
})

axiosIns.interceptors.request.use(
  config => {
    // Get token from localStorage
    const accessToken = useJwt.getToken();
    // If token is present add it to request's Authorization Header
    if (accessToken) {
      // eslint-disable-next-line no-param-reassign
      config.headers.Authorization = `${useJwt.jwtConfig.tokenType} ${accessToken}`
    }
    return config
  },
  error => Promise.reject(error),
)

// Shared across concurrent 403s so the user is only re-fetched once
let workspaceCheckInFlight = null

// A 403 may mean the selected workspace was removed (e.g. it is being deleted).
// Re-fetch the user and, if the workspace is gone, send them back to the picker.
const checkSelectedWorkspaceAvailable = () => {
  if (workspaceCheckInFlight) return workspaceCheckInFlight

  const selectedCompany = store.state.workspace.company
  if (!selectedCompany || !selectedCompany.company || !isUserLoggedIn()) return Promise.resolve()

  workspaceCheckInFlight = useJwt.getUser()
    .then(response => {
      const companies = response.data.companies || []
      if (companies.some(c => c.company === selectedCompany.company)) return

      store.commit('companies/SET_USER_COMPANIES', companies)
      store.commit('workspace/CLEAR_COMPANY')
      store.commit('appContext/CLEAR_SCENARIO')
      store.commit('appContext/CLEAR_MODEL')
      ability.update(initialAbility)

      Vue.$toast({
        component: ToastificationContent,
        position: 'top-right',
        props: {
          title: i18n.t('Workspace'),
          icon: 'AlertTriangleIcon',
          variant: 'warning',
          text: i18n.t('This workspace is no longer available'),
        },
      })
      Router.push({ name: 'workspace' })
    })
    .catch(() => {})
    .then(() => {
      workspaceCheckInFlight = null
    })
  return workspaceCheckInFlight
}

// Add request/response interceptor
axiosIns.interceptors.response.use(
  response => response,
  error => {
    const { config, response } = error
    const originalRequest = config

    if (response && response.status === 401) {
      if (!useJwt.isAlreadyFetchingAccessToken) {
        useJwt.isAlreadyFetchingAccessToken = true
        useJwt.refreshToken().then(r => {
          useJwt.isAlreadyFetchingAccessToken = false

          // Update accessToken in localStorage
          useJwt.setToken(r.data.access)
          //useJwt.setRefreshToken(r.data.refreshToken)

          useJwt.onAccessTokenFetched(r.data.access)
        }).catch(err => {
          useJwt.isAlreadyFetchingAccessToken = false
          // Remove userData from localStorage
          // ? You just removed token from localStorage. If you like, you can also make API call to backend to blacklist used token
          localStorage.removeItem(useJwt.jwtConfig.storageTokenKeyName)
          localStorage.removeItem(useJwt.jwtConfig.storageRefreshTokenKeyName)

          // Remove userData from localStorage
          localStorage.removeItem('userData')

          // Reset ability
          //ability.update(initialAbility)

          // Redirect to login page
          Router.push({ name: 'auth-login' })
        })
      }
      return new Promise(resolve => {
        useJwt.addSubscriber(accessToken => {
          // Make sure to assign accessToken according to your response.
          // Check: https://pixinvent.ticksy.com/ticket/2413870
          // Change Authorization header
          originalRequest.headers.Authorization = `${useJwt.jwtConfig.tokenType} ${accessToken}`
          resolve(axiosIns(originalRequest))
        })
      })
    }
    if (response && response.status === 403 && !(config && config.url === useJwt.jwtConfig.getUser)) {
      checkSelectedWorkspaceAvailable()
    }
    return Promise.reject(error)
  },
)


Vue.prototype.$http = axiosIns

export default axiosIns
